import { useState } from 'react';
import { Search, Users, Calendar, MessageCircle, Eye } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useUnifiedClientCustomers, useUnifiedClientStats, useUnifiedServiceHealth } from '@/hooks/useUnifiedClientManagement';
import { useDemoMode } from '@/hooks/useDemoMode';
// import { CustomerDetailModal } from './CustomerDetailModal'; // Removed
// import { DemoModeNotice } from './DemoModeNotice'; // Removed
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import type { ClientListFilters, ClientSummary } from '@/types/client-management.types';

// const CLIENT_ID = 'st-austin'; // Hardcoded for demo - unused

export const ClientCustomersView: React.FC = () => {
  const [filters, setFilters] = useState<ClientListFilters>({});
  const [selectedCustomer, setSelectedCustomer] = useState<ClientSummary | null>(null);
  const [pagination, setPagination] = useState({ limit: 50, offset: 0 });

  const { 
    isDemoMode, 
    enableDemoMode, 
    getDemoCustomers, 
    getDemoStats 
  } = useDemoMode();

  // Use unified hooks for real data
  const { data: customersData, isLoading, error } = useUnifiedClientCustomers(
    filters, 
    pagination, 
    { enabled: !isDemoMode }
  );
  const { data: stats } = useUnifiedClientStats({ enabled: !isDemoMode });
  const { data: healthData } = useUnifiedServiceHealth();

  // Use demo data when in demo mode
  const displayData = isDemoMode ? getDemoCustomers(filters) : customersData;
  const displayStats = isDemoMode ? getDemoStats() : stats;


  const handleFilterChange = (key: keyof ClientListFilters, value: string | boolean | number | undefined) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, offset: 0 })); // Reset pagination
  };

  const handleSearch = (value: string) => {
    handleFilterChange('search', value || undefined);
  };

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getStatusBadge = (leadStatus: string | undefined) => {
    if (!leadStatus) return <Badge variant="secondary">Sin estado</Badge>;
    
    const statusConfig: Record<string, { variant: 'default' | 'secondary' | 'destructive' | 'outline', className: string }> = {
      'Por calificar': { variant: 'outline', className: 'text-gray-600 border-gray-300' },
      'Frio': { variant: 'secondary', className: 'text-blue-600 bg-blue-50 border-blue-200' },
      'Tibio': { variant: 'outline', className: 'text-yellow-600 bg-yellow-50 border-yellow-300' },
      'Caliente': { variant: 'destructive', className: 'text-red-600 bg-red-50 border-red-300' },
      'Sin estado': { variant: 'outline', className: 'text-gray-500 bg-gray-50 border-gray-200' }
    };
    
    const config = statusConfig[leadStatus] || { variant: 'outline' as const, className: '' };
    
    return (
      <Badge variant={config.variant} className={config.className}>
        {leadStatus}
      </Badge>
    );
  };

  // Show demo mode notice if service is not available and not in demo mode
  if (healthData?.available === false && !isDemoMode) {
    return (
      <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
        {/* DemoModeNotice component is missing */}
        <p className="text-yellow-800">Demo mode notice placeholder</p>
        <Button onClick={enableDemoMode} className="mt-2" variant="outline">
          Enable Demo Mode
        </Button>
      </div>
    );
  }

  if (error && !isDemoMode) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-red-500 mb-2">Error al cargar los clientes</p>
          <p className="text-sm text-gray-600">{error.message}</p>
          <Button onClick={enableDemoMode} className="mt-4" variant="outline">
            Ver Datos Demo
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Demo Mode Notice */}
      {isDemoMode && (
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          {/* DemoModeNotice component is missing */}
          <p className="text-blue-800">Viewing demo data</p>
        </div>
      )}

      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Gestión de Clientes</h1>
          <p className="text-gray-600">Cliente: St Austin</p>
        </div>
      </div>

      {/* Stats Cards */}
      {displayStats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Clientes</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{displayStats.totalClients}</div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Citas Agendadas</CardTitle>
              <Calendar className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{displayStats.citasAgendadas}</div>
              <p className="text-xs text-muted-foreground">
                {displayStats.conversionRate}% conversión
              </p>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Asesor Respondió</CardTitle>
              <MessageCircle className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{displayStats.asesorRespondieron}</div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Tasa de Respuesta</CardTitle>
              <MessageCircle className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {displayStats.totalClients > 0 ? 
                  Math.round((displayStats.asesorRespondieron / displayStats.totalClients) * 100) : 0}%
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Filtros</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Buscar por nombre, email, teléfono..."
                className="pl-10"
                value={filters.search || ''}
                onChange={(e) => handleSearch(e.target.value)}
              />
            </div>
            
            <Select onValueChange={(value) => handleFilterChange('leadStatus', value === 'all' ? undefined : value)}>
              <SelectTrigger>
                <SelectValue placeholder="Estado del Lead" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los estados</SelectItem>
                <SelectItem value="Por calificar">Por calificar</SelectItem>
                <SelectItem value="Frio">Frío</SelectItem>
                <SelectItem value="Tibio">Tibio</SelectItem>
                <SelectItem value="Caliente">Caliente</SelectItem>
                <SelectItem value="Sin estado">Sin estado</SelectItem>
              </SelectContent>
            </Select>
            
            <Select onValueChange={(value) => handleFilterChange('citaAgendada', value === 'all' ? undefined : value === 'true')}>
              <SelectTrigger>
                <SelectValue placeholder="Cita Agendada" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas</SelectItem>
                <SelectItem value="true">Con cita</SelectItem>
                <SelectItem value="false">Sin cita</SelectItem>
              </SelectContent>
            </Select>
            
            <Select onValueChange={(value) => handleFilterChange('asesorRespondio', value === 'all' ? undefined : value === 'true')}>
              <SelectTrigger>
                <SelectValue placeholder="Asesor Respondió" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="true">Respondió</SelectItem>
                <SelectItem value="false">No respondió</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Customers Table */}
      <Card>
        <CardHeader>
          <CardTitle>Lista de Clientes</CardTitle>
          <CardDescription>
            Mostrando {displayData?.customers?.length || 0} clientes en esta página
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading && !isDemoMode ? (
            <div className="flex items-center justify-center h-32">
              <LoadingSpinner />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nombre</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Teléfono</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead>Último Mensaje</TableHead>
                  <TableHead>Cita</TableHead>
                  <TableHead>Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {displayData?.customers?.map((customer: ClientSummary) => (
                  <TableRow key={customer.idCliente || customer.id}>
                    <TableCell className="font-medium">
                      {customer.nombre || ''} {customer.apellido || ''}
                    </TableCell>
                    <TableCell>{customer.correoElectronico || 'N/A'}</TableCell>
                    <TableCell>{customer.numeroTelefono || 'N/A'}</TableCell>
                    <TableCell>{getStatusBadge(customer.leadStatus)}</TableCell>
                    <TableCell>{formatDate(customer.fechaUltimoMensaje)}</TableCell>
                    <TableCell>
                      <Badge variant={customer.citaAgendada ? 'default' : 'secondary'}>
                        {customer.citaAgendada ? 'Sí' : 'No'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setSelectedCustomer(customer)}
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        Ver Detalle
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {displayData && displayData.customers && displayData.customers.length > 0 && (
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Mostrar:</span>
              <Select 
                value={pagination.limit.toString()} 
                onValueChange={(value) => setPagination(prev => ({ ...prev, limit: parseInt(value), offset: 0 }))}
              >
                <SelectTrigger className="w-20 h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="25">25</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                  <SelectItem value="200">200</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <span className="text-sm text-gray-600">
              Mostrando {pagination.offset + 1} - {pagination.offset + (displayData?.customers?.length || 0)} clientes
              {displayData?.total && ` de ${displayData.total}`}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => setPagination(prev => ({ ...prev, offset: Math.max(0, prev.offset - prev.limit) }))}
              disabled={pagination.offset === 0}
            >
              Anterior
            </Button>
            <span className="text-sm text-gray-600 px-3">
              Página {Math.floor(pagination.offset / pagination.limit) + 1}
            </span>
            <Button
              variant="outline"
              onClick={() => setPagination(prev => ({ ...prev, offset: prev.offset + prev.limit }))}
              disabled={!displayData?.customers || displayData.customers.length < pagination.limit}
            >
              Siguiente
            </Button>
          </div>
        </div>
      )}

      {/* Customer Detail Modal */}
      {selectedCustomer && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center">
          {/* CustomerDetailModal component is missing */}
          <div className="bg-white p-6 rounded-lg">
            <h3 className="text-lg font-semibold mb-4">Customer Detail Modal Placeholder</h3>
            <p>Customer: {`${selectedCustomer.nombre || ''} ${selectedCustomer.apellido || ''}`.trim()}</p>
            <Button onClick={() => setSelectedCustomer(null)} className="mt-4">
              Close
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};